import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Download, PencilSimple, X, Warning, Path } from "@phosphor-icons/react";

const ZONES = ["1A", "1B", "2", "3", "4", "5"];
const MODE_LABEL = { day: "Jour", week: "Semaine", month: "Mois" };

function toISO(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfWeek(d) {
  const c = new Date(d);
  const day = (c.getDay() + 6) % 7; // Monday-based
  c.setDate(c.getDate() - day);
  return c;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmtDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
}

/**
 * Compta report page — journalier / hebdo / mensuel toggle + CSV download +
 * inline override editor for the manual "zone + panier" per (user, day).
 */
export default function PayrollReport() {
  const { user } = useAuth();
  const canSee = ["admin", "compta", "associate", "manager"].includes(user?.role);

  const [mode, setMode] = useState("day"); // day | week | month
  const [anchor, setAnchor] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // {rows} for day or {users} for week/month
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {row, date}

  const { start, end } = useMemo(() => {
    if (mode === "day") return { start: toISO(anchor), end: toISO(anchor) };
    if (mode === "week") {
      const s = startOfWeek(anchor);
      return { start: toISO(s), end: toISO(addDays(s, 6)) };
    }
    return { start: toISO(startOfMonth(anchor)), end: toISO(endOfMonth(anchor)) };
  }, [mode, anchor]);

  const load = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === "day") {
        const r = await api.get(`/payroll/daily?date=${start}`);
        setData(r.data);
      } else {
        const r = await api.get(`/payroll/period?start=${start}&end=${end}`);
        setData(r.data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [canSee, mode, start, end]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta) => {
    const c = new Date(anchor);
    if (mode === "day") c.setDate(c.getDate() + delta);
    else if (mode === "week") c.setDate(c.getDate() + delta * 7);
    else c.setMonth(c.getMonth() + delta);
    setAnchor(c);
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get(`/payroll/export.csv?start=${start}&end=${end}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `navette_paie_${start}_${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exporté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export impossible");
    }
  };

  if (!canSee) {
    return (
      <div className="p-6 text-white/60" data-testid="payroll-page">
        Cette page est réservée à la comptabilité et à la direction.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">Paie</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold">Paniers & Indemnités de trajet</h1>
          <div className="text-xs text-white/50 mt-1">
            Zone calculée à vol d&apos;oiseau : bureau → chantier le plus loin du jour.
          </div>
        </div>
        <button
          onClick={downloadCsv}
          className="h-10 px-3 rounded-lg bg-[#005CFF] hover:bg-[#0047CC] font-semibold text-sm flex items-center gap-2"
          data-testid="payroll-export-csv"
        >
          <Download size={15} weight="bold" /> CSV navette
        </button>
      </div>

      {/* Toggle mode + navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex rounded-lg bg-white/5 border border-white/10 p-1">
          {Object.entries(MODE_LABEL).map(([k, l]) => (
            <button
              key={k}
              onClick={() => setMode(k)}
              className={`px-3.5 h-8 rounded-md text-xs font-semibold uppercase tracking-widest transition-colors ${
                mode === k ? "bg-[#005CFF] text-white" : "text-white/60 hover:text-white"
              }`}
              data-testid={`payroll-mode-${k}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="payroll-prev">
            <CaretLeft size={14} />
          </button>
          <div className="text-sm tabular-nums px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" data-testid="payroll-range">
            {mode === "day" ? fmtDay(start) : `${fmtDay(start)} → ${fmtDay(end)}`}
          </div>
          <button onClick={() => shift(1)} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="payroll-next">
            <CaretRight size={14} />
          </button>
          <button
            onClick={() => setAnchor(new Date())}
            className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-xs uppercase tracking-widest"
            data-testid="payroll-today"
          >
            Aujourd&apos;hui
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-300">{error}</div>}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 rounded-xl bg-white/5 animate-pulse" />)}
        </div>
      ) : mode === "day" ? (
        <DayTable data={data} onEdit={(row) => setEditing({ row, date: start })} onReload={load} />
      ) : (
        <PeriodTable data={data} onEdit={(row) => setEditing({ row, date: row.date })} onReload={load} />
      )}

      {editing && (
        <OverrideDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Day table
// ---------------------------------------------------------------------------
function DayTable({ data, onEdit, onReload }) {
  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-8 text-center text-white/50" data-testid="payroll-empty">
        Aucun tech planifié ce jour-là.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="payroll-day-table">
          <thead className="bg-white/[0.04] text-[10px] uppercase tracking-widest text-white/50">
            <tr>
              <th className="px-3 py-2.5 text-left">Salarié</th>
              <th className="px-3 py-2.5 text-left">Chantier le + loin</th>
              <th className="px-3 py-2.5 text-right">Km</th>
              <th className="px-3 py-2.5 text-center">Zone</th>
              <th className="px-3 py-2.5 text-center">Panier</th>
              <th className="px-3 py-2.5 text-center">Adresse ⚠️</th>
              <th className="px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const suspicious = (r.missing_address || []).filter((m) => m.reason === "distance_suspicious");
              const emptyAddr = (r.missing_address || []).filter((m) => m.reason !== "distance_suspicious");
              return (
                <tr key={r.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`payroll-row-${r.user_id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{r.user_name || "—"}</div>
                    {r.overridden && <div className="text-[9px] uppercase tracking-widest text-amber-400">ajusté manuellement</div>}
                  </td>
                  <td className="px-3 py-2.5 text-white/70 truncate max-w-[220px]">{r.farthest_chantier || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-white/70">{r.farthest_km ?? "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    <ZoneBadge zone={r.zone} />
                  </td>
                  <td className="px-3 py-2.5 text-center tabular-nums">{r.panier || 0}</td>
                  <td className="px-3 py-2.5 text-center">
                    {(r.missing_address || []).length > 0 ? (
                      <div className="inline-flex flex-col items-center text-[10px] gap-0.5">
                        {suspicious.length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-red-300"
                            title={suspicious.map((s) => `${s.chantier_name || s.title} — ${s.km} km (adresse à corriger)`).join("\n")}
                          >
                            <Warning size={12} /> {suspicious.length} suspect{suspicious.length > 1 ? "s" : ""}
                          </span>
                        )}
                        {emptyAddr.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-300">
                            <Warning size={12} /> {emptyAddr.length} sans adresse
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-white/30">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      onClick={() => onEdit(r)}
                      className="h-7 px-2 rounded-md bg-white/5 hover:bg-white/10 text-xs inline-flex items-center gap-1"
                      data-testid={`payroll-edit-${r.user_id}`}
                    >
                      <PencilSimple size={11} /> Ajuster
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Period (week / month) table
// ---------------------------------------------------------------------------
function PeriodTable({ data, onEdit, onReload }) {
  const users = data?.users || [];
  if (!users.length) {
    return (
      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-8 text-center text-white/50" data-testid="payroll-empty">
        Aucune activité sur cette période.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payroll-period-table">
            <thead className="bg-white/[0.04] text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="px-3 py-2.5 text-left">Salarié</th>
                <th className="px-3 py-2.5 text-right">Jours</th>
                <th className="px-3 py-2.5 text-right">Heures</th>
                <th className="px-3 py-2.5 text-right">H. sup</th>
                <th className="px-3 py-2.5 text-right">Paniers</th>
                {ZONES.map((z) => <th key={z} className="px-2 py-2.5 text-right">Zone {z}</th>)}
                <th className="px-3 py-2.5 text-right">Km total</th>
                <th className="px-3 py-2.5 text-right">CP</th>
                <th className="px-3 py-2.5 text-right">RTT</th>
                <th className="px-3 py-2.5 text-right">Maladie</th>
                <th className="px-3 py-2.5 text-right">Sans adr.</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const ab = u.absences || {};
                return (
                  <tr key={u.user_id} className="border-t border-white/5" data-testid={`payroll-sum-${u.user_id}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{u.user_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40">{u.role}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{u.days_worked}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-white/70" data-testid={`payroll-hours-${u.user_id}`}>{u.worked_hm || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold" data-testid={`payroll-overtime-${u.user_id}`}>
                      {u.overtime_min > 0 ? <span className="text-[#4ADE80]">{u.overtime_hm}</span> : <span className="text-white/40">0h00</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{u.paniers}</td>
                    {ZONES.map((z) => (
                      <td key={z} className="px-2 py-2.5 text-right tabular-nums">{u.zones?.[z] || 0}</td>
                    ))}
                    <td className="px-3 py-2.5 text-right tabular-nums text-white/70">{u.total_km}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`payroll-cp-${u.user_id}`}>{ab.paid ? <span className="text-[#7CB2FF]">{ab.paid}</span> : "0"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`payroll-rtt-${u.user_id}`}>{ab.rtt ? <span className="text-[#7CB2FF]">{ab.rtt}</span> : "0"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`payroll-sick-${u.user_id}`}>{ab.sick ? <span className="text-amber-300">{ab.sick}</span> : "0"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {u.missing_address_days > 0 ? (
                        <span className="text-amber-300">{u.missing_address_days}</span>
                      ) : "0"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed day-by-day breakdown per user */}
      {users.map((u) => (
        <details key={`d-${u.user_id}`} className="rounded-xl bg-white/[0.02] border border-white/10">
          <summary className="cursor-pointer px-4 py-3 text-sm flex items-center justify-between hover:bg-white/[0.03]" data-testid={`payroll-user-details-${u.user_id}`}>
            <span className="font-medium">{u.user_name}</span>
            <span className="text-xs text-white/50">
              {u.days.length} jour(s) — {u.paniers} panier(s) — {u.total_km} km — {u.worked_hm || "0h00"}
              {u.overtime_min > 0 && <span className="ml-2 text-[#4ADE80]">+{u.overtime_hm} sup</span>}
            </span>
          </summary>
          <div className="px-3 pb-3 space-y-3">
            {/* Weekly overtime breakdown */}
            {(u.weeks || []).length > 0 && (
              <div className="rounded-lg bg-white/[0.03] border border-white/10 overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="text-[9px] uppercase tracking-widest text-white/40 bg-white/[0.03]">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Semaine</th>
                      <th className="px-2 py-1.5 text-right">Heures travaillées</th>
                      <th className="px-2 py-1.5 text-right">Base</th>
                      <th className="px-2 py-1.5 text-right">Heures sup</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.weeks.map((wk) => (
                      <tr key={wk.week_index} className="border-t border-white/5" data-testid={`payroll-week-${u.user_id}-${wk.week_index}`}>
                        <td className="px-2 py-1.5 text-white/70">S{wk.week_index} · {wk.monday} → {wk.sunday}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{wk.worked_hm}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-white/40">39h00</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                          {wk.overtime_min > 0 ? <span className="text-[#4ADE80]">{wk.overtime_hm}</span> : <span className="text-white/30">0h00</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Daily breakdown */}
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-white/40">
                <tr>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Chantier</th>
                  <th className="px-2 py-1.5 text-right">Km</th>
                  <th className="px-2 py-1.5 text-center">Zone</th>
                  <th className="px-2 py-1.5 text-center">P</th>
                  <th className="px-2 py-1.5 text-right">Heures</th>
                  <th className="px-2 py-1.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {u.days.map((d) => (
                  <tr key={d.date} className="border-t border-white/5" data-testid={`payroll-detail-${u.user_id}-${d.date}`}>
                    <td className="px-2 py-1.5 tabular-nums text-white/70">{d.date}</td>
                    <td className="px-2 py-1.5 truncate max-w-[240px]">{d.farthest_chantier || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-white/60">{d.farthest_km ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center"><ZoneBadge zone={d.zone} small /></td>
                    <td className="px-2 py-1.5 text-center tabular-nums">{d.panier || 0}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-white/70">
                      {d.worked_hm || "—"}
                      {d.worked_source && d.worked_source !== "none" && (
                        <span className="ml-1 text-[9px] text-white/40 uppercase">{d.worked_source === "depot" ? "dépôt" : d.worked_source === "chrono" ? "chrono" : "prog."}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => onEdit({ ...d, user_id: u.user_id, user_name: u.user_name })}
                        className="h-6 px-2 rounded-md bg-white/5 hover:bg-white/10 text-[10px] inline-flex items-center gap-1"
                      >
                        <PencilSimple size={9} /> Ajuster
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
}

function ZoneBadge({ zone, small }) {
  if (!zone) return <span className="text-white/30">—</span>;
  return (
    <span className={`inline-flex items-center justify-center rounded-md bg-[#005CFF]/20 text-[#7CB2FF] font-bold tracking-widest ${small ? "text-[9px] px-1.5 h-4" : "text-[10px] px-2 h-5"}`}>
      {zone}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Override dialog
// ---------------------------------------------------------------------------
function OverrideDialog({ editing, onClose, onSaved }) {
  const { row, date } = editing;
  const [zone, setZone] = useState(row.zone || row.auto_zone || "");
  const [panier, setPanier] = useState(Number(row.panier ?? row.auto_panier ?? 0));
  const [lunchMin, setLunchMin] = useState(
    row.lunch_min != null ? String(row.lunch_min) : "",
  );
  const [note, setNote] = useState(row.override_note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const parsedLunch =
        lunchMin === "" || lunchMin == null ? null : Math.max(0, Math.min(180, parseInt(lunchMin, 10) || 0));
      await api.post("/payroll/override", {
        user_id: row.user_id,
        date,
        zone: zone || null,
        panier,
        lunch_min: parsedLunch,
        note: note || null,
      });
      toast.success("Ajustement enregistré");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post("/payroll/override", { user_id: row.user_id, date, zone: null, panier: null, lunch_min: null, note: null });
      toast.success("Ajustement supprimé — retour au calcul auto");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4" data-testid="payroll-override-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-white/10 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Ajuster</div>
            <div className="font-semibold">{row.user_name} · {date}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Auto-calculé</div>
            <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-white/70">Chantier le + loin</span>
                <span>{row.farthest_chantier || "—"}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-white/70 flex items-center gap-1"><Path size={11} /> Distance</span>
                <span className="tabular-nums">{row.farthest_km ?? "—"} km</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-white/70">Zone auto</span>
                <ZoneBadge zone={row.auto_zone} />
              </div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Zone appliquée</div>
            <div className="grid grid-cols-6 gap-1.5">
              {ZONES.map((z) => (
                <button
                  key={z}
                  onClick={() => setZone(z)}
                  className={`h-9 rounded-md text-xs font-bold tracking-widest transition-colors ${
                    zone === z ? "bg-[#005CFF] text-white" : "bg-white/5 hover:bg-white/10 text-white/70"
                  }`}
                  data-testid={`payroll-zone-btn-${z}`}
                >
                  {z}
                </button>
              ))}
            </div>
            {zone && zone !== (row.auto_zone || "") && (
              <button onClick={() => setZone(row.auto_zone || "")} className="text-[10px] text-white/50 hover:text-white mt-1.5">
                Remettre la zone automatique ({row.auto_zone || "—"})
              </button>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Panier repas</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[0, 1].map((n) => (
                <button
                  key={n}
                  onClick={() => setPanier(n)}
                  className={`h-9 rounded-md text-xs font-bold transition-colors ${
                    panier === n ? "bg-[#005CFF] text-white" : "bg-white/5 hover:bg-white/10 text-white/70"
                  }`}
                  data-testid={`payroll-panier-btn-${n}`}
                >
                  {n} panier{n > 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center justify-between">
              <span>Pause déjeuner (min)</span>
              <span className="text-white/30 normal-case tracking-normal">Défaut : 90 min</span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {[0, 30, 45, 60, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => setLunchMin(String(n))}
                  className={`h-9 rounded-md text-xs font-bold tracking-widest transition-colors ${
                    String(lunchMin) === String(n) ? "bg-[#005CFF] text-white" : "bg-white/5 hover:bg-white/10 text-white/70"
                  }`}
                  data-testid={`payroll-lunch-btn-${n}`}
                >
                  {n === 0 ? "Aucune" : `${n}′`}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min="0"
                max="180"
                value={lunchMin}
                onChange={(e) => setLunchMin(e.target.value)}
                placeholder="Auto (90)"
                className="flex-1 h-9 rounded-lg bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#005CFF] tabular-nums"
                data-testid="payroll-override-lunch-min"
              />
              {lunchMin !== "" && (
                <button
                  onClick={() => setLunchMin("")}
                  className="text-[10px] text-white/50 hover:text-white px-2"
                  data-testid="payroll-override-lunch-reset"
                >
                  Auto
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Note</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Optionnel — raison de l'ajustement"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#005CFF]"
              data-testid="payroll-override-note"
            />
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-white/[0.02]">
          <button
            onClick={reset}
            disabled={saving || !row.overridden}
            className="h-9 px-3 rounded-lg text-xs text-white/60 hover:text-white disabled:opacity-40"
            data-testid="payroll-override-reset"
          >
            Retour auto
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm bg-white/5 hover:bg-white/10">Annuler</button>
            <button onClick={save} disabled={saving} className="h-9 px-4 rounded-lg text-sm font-semibold bg-[#005CFF] hover:bg-[#0047CC] disabled:opacity-50" data-testid="payroll-override-save">
              {saving ? "…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
